/**
 * Customer Portal (shared) - USA area code catalog and validation helpers.
 */
export const USA_AREA_CODES_BY_STATE = {
  AL: ['205', '251', '256', '334', '938'],
  AK: ['907'],
  AZ: ['480', '520', '602', '623', '928'],
  AR: ['327', '479', '501', '870'],
  CA: [
    '209',
    '213',
    '310',
    '323',
    '341',
    '350',
    '357',
    '369',
    '408',
    '415',
    '424',
    '442',
    '510',
    '530',
    '559',
    '562',
    '619',
    '626',
    '628',
    '650',
    '657',
    '661',
    '669',
    '707',
    '714',
    '747',
    '760',
    '805',
    '818',
    '820',
    '831',
    '858',
    '909',
    '916',
    '925',
    '949',
    '951'
  ],
  CO: ['303', '719', '720', '970'],
  CT: ['203', '475', '860', '959'],
  DE: ['302'],
  FL: [
    '239',
    '305',
    '321',
    '324',
    '352',
    '386',
    '407',
    '448',
    '561',
    '656',
    '689',
    '727',
    '754',
    '772',
    '786',
    '813',
    '850',
    '863',
    '904',
    '941',
    '954'
  ],
  GA: ['229', '404', '470', '478', '678', '706', '762', '770', '912'],
  HI: ['808'],
  ID: ['208', '986'],
  IL: ['217', '224', '309', '312', '331', '618', '630', '708', '773', '779', '815', '847', '872'],
  IN: ['219', '260', '317', '463', '574', '765', '812', '930'],
  IA: ['319', '515', '563', '641', '712'],
  KS: ['316', '620', '785', '913'],
  KY: ['270', '364', '502', '606', '859'],
  LA: ['225', '318', '337', '504', '985'],
  ME: ['207'],
  MD: ['240', '301', '307', '410', '443', '667'],
  MA: ['339', '351', '413', '508', '617', '774', '781', '857', '978'],
  MI: ['231', '248', '269', '313', '517', '586', '616', '734', '810', '906', '947', '989'],
  MN: ['218', '320', '507', '612', '651', '763', '952'],
  MS: ['228', '601', '662', '769'],
  MO: ['314', '417', '573', '636', '660', '816'],
  MT: ['406'],
  NE: ['308', '402', '531'],
  NV: ['702', '775', '725'],
  NH: ['603'],
  NJ: ['201', '551', '609', '640', '732', '848', '856', '908', '973'],
  NM: ['505', '575'],
  NY: [
    '212',
    '315',
    '329',
    '332',
    '347',
    '363',
    '516',
    '518',
    '607',
    '631',
    '646',
    '680',
    '716',
    '718',
    '845',
    '914',
    '917',
    '929'
  ],
  NC: ['252', '336', '704', '828', '910', '919', '980', '984'],
  ND: ['701'],
  OH: ['216', '220', '234', '330', '380', '419', '440', '513', '567', '614', '740', '930'],
  OK: ['405', '539', '580', '918'],
  OR: ['458', '503', '541', '971'],
  PA: ['215', '267', '272', '412', '484', '570', '610', '717', '724', '814', '835', '878'],
  RI: ['401'],
  SC: ['803', '843', '854', '864'],
  SD: ['605'],
  TN: ['423', '615', '629', '731', '865', '901', '931'],
  TX: [
    '210',
    '214',
    '254',
    '281',
    '325',
    '346',
    '361',
    '409',
    '430',
    '432',
    '469',
    '512',
    '682',
    '713',
    '737',
    '806',
    '817',
    '830',
    '832',
    '903',
    '915',
    '936',
    '940',
    '956',
    '972',
    '979'
  ],
  UT: ['385', '435', '801'],
  VT: ['802'],
  VA: ['276', '434', '540', '571', '703', '757', '804'],
  WA: ['206', '253', '360', '425', '509', '564'],
  WV: ['304', '681'],
  WI: ['262', '353', '414', '534', '608', '715', '920'],
  WY: ['307']
} as const;

export type UsaStateCode = keyof typeof USA_AREA_CODES_BY_STATE;

export type PreferredAreaCodeValidationInput = {
  country: 'USA' | 'CANADA' | 'UK';
  preferredAreaCode: string | null | undefined;
  selectedUsState: string | null | undefined;
};

export type PreferredAreaCodeValidationResult = {
  normalizedAreaCode: string | null;
  normalizedUsState: UsaStateCode | null;
  error: string | null;
};

const USA_STATE_CODE_SET = new Set<string>(Object.keys(USA_AREA_CODES_BY_STATE));
const USA_AREA_CODE_SET = new Set<string>(Object.values(USA_AREA_CODES_BY_STATE).flat());

export const USA_STATE_OPTIONS = Object.keys(USA_AREA_CODES_BY_STATE).sort() as UsaStateCode[];

export function normalizeAreaCodeInput(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '');
}

export function isUsStateCode(value: string | null | undefined): value is UsaStateCode {
  return !!value && USA_STATE_CODE_SET.has(value);
}

export function isValidUsAreaCode(value: string): boolean {
  return /^\d{3}$/.test(value) && USA_AREA_CODE_SET.has(value);
}

export function isAreaCodeInUsState(areaCode: string, stateCode: UsaStateCode): boolean {
  return USA_AREA_CODES_BY_STATE[stateCode].includes(areaCode);
}

export function getAreaCodesForUsState(stateCode: string | null | undefined): string[] {
  if (!isUsStateCode(stateCode)) return [];
  return [...USA_AREA_CODES_BY_STATE[stateCode]];
}

export function validatePreferredAreaCode(input: PreferredAreaCodeValidationInput): PreferredAreaCodeValidationResult {
  const normalizedAreaCodeRaw = normalizeAreaCodeInput(input.preferredAreaCode);
  const hasAreaCode = normalizedAreaCodeRaw.length > 0;
  const normalizedUsState = isUsStateCode(input.selectedUsState) ? input.selectedUsState : null;

  if (input.country === 'UK') {
    if (hasAreaCode) {
      return {
        normalizedAreaCode: null,
        normalizedUsState: null,
        error: 'Preferred area code is not supported for UK requests.'
      };
    }
    return {
      normalizedAreaCode: null,
      normalizedUsState: null,
      error: null
    };
  }

  if (input.country === 'CANADA') {
    if (!hasAreaCode) {
      return {
        normalizedAreaCode: null,
        normalizedUsState: null,
        error: null
      };
    }
    if (!/^\d{3}$/.test(normalizedAreaCodeRaw)) {
      return {
        normalizedAreaCode: null,
        normalizedUsState: null,
        error: 'Preferred area code for Canada must be exactly 3 digits.'
      };
    }
    return {
      normalizedAreaCode: normalizedAreaCodeRaw,
      normalizedUsState: null,
      error: null
    };
  }

  if (!hasAreaCode) {
    return {
      normalizedAreaCode: null,
      normalizedUsState,
      error: null
    };
  }
  if (!/^\d{3}$/.test(normalizedAreaCodeRaw)) {
    return {
      normalizedAreaCode: null,
      normalizedUsState,
      error: 'Preferred area code for USA must be exactly 3 digits.'
    };
  }
  if (!isValidUsAreaCode(normalizedAreaCodeRaw)) {
    return {
      normalizedAreaCode: null,
      normalizedUsState,
      error: 'Preferred area code is not a valid USA area code.'
    };
  }
  if (normalizedUsState && !isAreaCodeInUsState(normalizedAreaCodeRaw, normalizedUsState)) {
    return {
      normalizedAreaCode: null,
      normalizedUsState,
      error: `Area code ${normalizedAreaCodeRaw} is not valid for state ${normalizedUsState}.`
    };
  }
  return {
    normalizedAreaCode: normalizedAreaCodeRaw,
    normalizedUsState,
    error: null
  };
}
